import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type MeasureDataByEntity } from '../../../../../api/work-periods-client/work-periods-client.type';
import {
  useEntities,
  useEntityNames,
  useSelectedMeasureOverTime,
  useSelectedMeasureOverTimeTrend,
} from '../../../../../store/process-analysis-store/process-analysis-store.hooks';
import { LineChart } from '../../../components/line-chart/line-chart';
import { getUnitsLabel } from '../../../process-analysis.helpers';
import { useChartData, useSelectedMeasureMetadata } from './line-chart.hooks';

type Props = {
  data: MeasureDataByEntity | undefined;
};

export const KeyMeasuresOverTimeLineChart = forwardRef<HTMLDivElement, Props>(({ data }, ref) => {
  const { t } = useTranslation();
  const selectedMeasureOverTime = useSelectedMeasureOverTime();
  const selectedMeasureOverTimeTrend = useSelectedMeasureOverTimeTrend();
  const entities = useEntities();
  const entityNames = useEntityNames();

  const chartData = useChartData(data);
  const selectedMeasureMetadata = useSelectedMeasureMetadata();

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const unitsLabel = useMemo(() => {
    return getUnitsLabel(selectedMeasureMetadata?.measure_units || null, t);
  }, [selectedMeasureMetadata, t]);

  const formatValue = useCallback(
    (value: number | null) => {
      if (value === null || value === undefined) {
        return t('common.labels.not_available');
      }

      return `${value} ${unitsLabel}`;
    },
    [unitsLabel, t],
  );

  const selectedItems = entities.map((entityId) => ({ id: entityId, name: entityId }));

  const selectedTrends = selectedMeasureOverTimeTrend
    ? entities.map((entityId) => ({ id: entityId, name: entityId }))
    : [];

  if (!selectedMeasureOverTime) {
    return null;
  }

  return (
    <LineChart
      ref={ref}
      chartData={chartData}
      selectedItems={selectedItems}
      selectedTrends={selectedTrends}
      hoveredItem={hoveredItem}
      setHoveredItem={setHoveredItem}
      yAxisConfig={{
        label: unitsLabel,
      }}
      formatItemName={(itemId) => entityNames.entityNames[itemId] || itemId}
      formatValue={formatValue}
    />
  );
});
